import Text from 'components/base/Text';
import { Flex, Padding } from 'components/base/layout-components';
import { verticalScroller } from 'components/base/scroller';
import React from 'react';
import styled from 'styled-components';
import { TextField } from '@material-ui/core';
import _ from 'lodash';
import { getColor } from 'common/constants/_colors';
import Dropdown from 'components/base/Dropdown';
import { ACCOUNT_TYPES_MAP } from '@linearb/linjs-common';
import validator from 'validator';
import { isAdmin, isSuperUser } from 'common/services/accounts.service';
import { parseResponseMsg } from 'common/utils/utils';
import { ALL_ACCOUNTS_TYPES_OPTIONS } from 'common/constants/constants';
import { LBButton } from '@linearb/buzz';

const ScrolledContainer = styled(Flex)`
  ${verticalScroller()};
  max-height: 90rem;
`;
const FieldsContainer = styled.div`
  width: 35rem;
`;
const AccountTypeContainer = styled.div`
  width: 19rem;
  padding-top: 4.3rem;
  .MuiInputBase-root {
    width: 19rem;
    height: 2.6rem;
  }
`;
const TextFieldWrapper = styled(Padding)`
  border-radius: 4px;

  margin-top: 0.4rem;
  border: ${({ borderColor }) =>
    `1px solid ${
      borderColor ? getColor(borderColor) : getColor('botticelli')
    }`};
`;

const Title = ({ text, ...props }) => {
  return (
    <Flex items='center' {...props}>
      <Text size='x-large'>{text}</Text>
    </Flex>
  );
};

const getInputField = ({
  name,
  paddingTop = 'medium',
  value,
  onChange,
  validationMessage = null,
  dataCy = ''
}) => {
  return (
    <Padding t={paddingTop} b='medium'>
      <Text color='shuttleGray' size='medium'>
        {name}
      </Text>
      <TextFieldWrapper
        padding='x-small'
        borderColor={validationMessage ? 'torchRed' : null}
      >
        <TextField
          value={value}
          onChange={onChange}
          InputProps={{
            inputProps: {
              'data-cy': `invite-user-input-field-${dataCy}`,
              style: {
                fontSize: 14
              }
            },
            disableUnderline: true
          }}
        />
      </TextFieldWrapper>
    </Padding>
  );
};

const getAccountTypeSelector = ({ value, onChange, accountTypesOptions }) => {
  return (
    <AccountTypeContainer>
      <Dropdown
        id='demo-simple-select'
        dataCy='invite-user-role-picker'
        selectedValue={value}
        onChange={onChange}
        options={accountTypesOptions}
        InputProps={{
          inputProps: {
            style: {
              fontSize: 14
            }
          },
          disableUnderline: true
        }}
      />
    </AccountTypeContainer>
  );
};

const getSnackbarMessage = message => {
  return (
    <Flex direction='column'>
      <Text size='large' color='white'>
        {message}
      </Text>
    </Flex>
  );
};

const UserInviteModalWithViewer = ({
  onSubmit,
  toggleSnackBar,
  sendAccountInvitation,
  myAccount,
  closeModal = _.noop
}) => {
  const [name, setName] = React.useState('');
  const [email, setEmail] = React.useState('');

  const accountTypesOptions = React.useMemo(
    () =>
      ALL_ACCOUNTS_TYPES_OPTIONS.filter(
        item =>
          isAdmin(myAccount) ||
          isSuperUser(myAccount) ||
          item.value !== ACCOUNT_TYPES_MAP.Primary
      ),
    [myAccount]
  );

  const [accountTypeId, setAccountTypeId] = React.useState(
    ACCOUNT_TYPES_MAP.Viewer
  );
  const isCreateDisabled =
    email.trim() === '' || name.trim() === '' || !validator.isEmail(email);

  const sendInvitation = async () => {
    try {
      await sendAccountInvitation({
        email,
        invitee_name: name,
        account_type_id: accountTypeId
      });
      toggleSnackBar({
        message: getSnackbarMessage(
          'The invitation has been sent successfully.'
        ),
        variant: 'success'
      });
      closeModal();
      onSubmit();
    } catch (exp) {
      const errMsg = parseResponseMsg(exp, 'Failed, Unknown Error');

      toggleSnackBar({
        message: getSnackbarMessage(errMsg),
        variant: 'error'
      });
    }
  };

  return (
    <Flex direction='column'>
      <Title pt='medium' pl='medium' text='Invite new person' />

      <ScrolledContainer direction='column' pl='medium' pb='medium' pr='medium'>
        <Flex justify='space-between'>
          <FieldsContainer>
            {getInputField({
              name: 'Name:',
              value: name,
              dataCy: 'name',
              onChange: e => setName(e.target.value)
            })}
            {getInputField({
              name: 'Email:',
              paddingTop: null,
              value: email,
              dataCy: 'email',
              onChange: e => setEmail(e.target.value),
              validationMessage:
                email.trim() !== '' && !validator.isEmail(email)
                  ? 'Email is not valid'
                  : null
            })}
          </FieldsContainer>
          {getAccountTypeSelector({
            value: accountTypeId,
            onChange: e => setAccountTypeId(e),
            accountTypesOptions
          })}
        </Flex>

        <Flex justify='flex-end'>
          <Padding r='small'>
            <LBButton
              variant='outline'
              data-cy='invite-user-modal-cancel-button'
              onClick={closeModal}
            >
              Cancel
            </LBButton>
          </Padding>

          <LBButton
            data-cy='invite-user-modal-send-button'
            onClick={sendInvitation}
            disabled={isCreateDisabled}
          >
            Send
          </LBButton>
        </Flex>
      </ScrolledContainer>
    </Flex>
  );
};

export default UserInviteModalWithViewer;
